/**
 * ContentPreviewLanding
 *
 * Entry page for teacher content preview feature.
 * Displays all 3 programs (Yellow, Green, Access) as cards.
 */

import React, { useEffect, useState } from "react";
import {
  Container,
  Box,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Skeleton,
  Alert,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";

// Icons
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchoolIcon from "@mui/icons-material/School";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import StarIcon from "@mui/icons-material/Star";

// Services & Context
import {
  previewService,
  PreviewProgramSummary,
} from "../../../services/previewService";
import { useAuth } from "../../../context/AuthContext";

// ============================================================================
// Styled Components
// ============================================================================

const PageHeader = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const ProgramCard = styled(Card)<{ programcolor: string }>(
  ({ theme, programcolor }) => ({
    height: "100%",
    display: "flex",
    flexDirection: "column",
    transition: "transform 0.2s ease-in-out, box-shadow 0.2s ease-in-out",
    borderTop: `4px solid ${programcolor}`,
    "&:hover": {
      transform: "translateY(-4px)",
      boxShadow: theme.shadows[8],
    },
  })
);

const ProgramIcon = styled(Box)<{ programcolor: string }>(
  ({ programcolor }) => ({
    width: 64,
    height: 64,
    borderRadius: "50%",
    backgroundColor: `${programcolor}15`,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 16,
    "& svg": {
      fontSize: 32,
      color: programcolor,
    },
  })
);

const StatsChip = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

// ============================================================================
// Constants
// ============================================================================

const PROGRAM_COLORS: Record<string, string> = {
  yellow: "#FFC107",
  green: "#4CAF50",
  access: "#2196F3",
};

const PROGRAM_ICONS: Record<string, React.ReactNode> = {
  yellow: <SchoolIcon />,
  green: <MenuBookIcon />,
  access: <StarIcon />,
};

// ============================================================================
// Component
// ============================================================================

const ContentPreviewLanding: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // State
  const [programs, setPrograms] = useState<PreviewProgramSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get teacher's assigned program (if any) from their classrooms
  const teacherAssignedProgram =
    user?.myClassroom?.programName?.toLowerCase() || null;

  // Fetch programs on mount
  useEffect(() => {
    const fetchPrograms = async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await previewService.getPrograms();
        setPrograms(data);
      } catch (err: any) {
        console.error("Error fetching programs:", err);
        setError(
          err.response?.status === 403
            ? "You don't have permission to access content preview. This feature is only available for teachers."
            : "Failed to load programs. Please try again."
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPrograms();
  }, []);

  // Navigate to program detail
  const handleViewProgram = (programId: string) => {
    navigate(`/teacher/preview/${programId}`);
  };

  // ============================================================================
  // Render
  // ============================================================================

  return (
    <Container maxWidth="lg">
      {/* Header */}
      <PageHeader>
        <Box display="flex" alignItems="center" gap={2} mb={1}>
          <VisibilityIcon color="primary" sx={{ fontSize: 32 }} />
          <Typography variant="h4" fontWeight="bold">
            Content Preview
          </Typography>
        </Box>
        <Typography variant="body1" color="text.secondary">
          Browse all program content for lesson preparation. View learning
          materials and quiz questions across all EAP programs.
        </Typography>
      </PageHeader>

      {/* Error Alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Program Cards */}
      <Grid container spacing={3}>
        {loading ? (
          // Loading skeletons
          <>
            {[1, 2, 3].map((i) => (
              <Grid item xs={12} md={4} key={i}>
                <Card>
                  <CardContent>
                    <Skeleton
                      variant="circular"
                      width={64}
                      height={64}
                      sx={{ mb: 2 }}
                    />
                    <Skeleton variant="text" width="60%" height={32} />
                    <Skeleton variant="text" width="40%" />
                    <Skeleton variant="text" width="100%" sx={{ mt: 2 }} />
                    <Skeleton variant="text" width="100%" />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </>
        ) : (
          // Program cards
          programs.map((program) => {
            const programId = program.programId.toLowerCase();
            const color = PROGRAM_COLORS[programId] || "#757575";
            const isAssigned = teacherAssignedProgram === programId;

            return (
              <Grid item xs={12} md={4} key={program.programId}>
                <ProgramCard programcolor={color}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    {/* Program Icon */}
                    <ProgramIcon programcolor={color}>
                      {PROGRAM_ICONS[programId] || <SchoolIcon />}
                    </ProgramIcon>

                    {/* Program Name & Badge */}
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                      <Typography variant="h5" fontWeight="bold">
                        {program.programName}
                      </Typography>
                      {isAssigned && (
                        <Chip
                          label="Your Program"
                          size="small"
                          color="primary"
                          variant="outlined"
                        />
                      )}
                    </Box>

                    {/* CEFR Level */}
                    <Typography
                      variant="subtitle1"
                      color="text.secondary"
                      gutterBottom
                    >
                      {program.cefrLevel}
                    </Typography>

                    {/* Description */}
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 2 }}
                    >
                      {program.description}
                    </Typography>

                    {/* Stats */}
                    <Box>
                      <StatsChip
                        label={`${program.totalWeeks} Weeks`}
                        size="small"
                        variant="outlined"
                      />
                      <StatsChip
                        label={`${program.modulesPerWeek} Modules/Week`}
                        size="small"
                        variant="outlined"
                      />
                    </Box>
                  </CardContent>

                  {/* Action */}
                  <CardActions sx={{ p: 2, pt: 0 }}>
                    <Button
                      variant="contained"
                      fullWidth
                      onClick={() => handleViewProgram(program.programId)}
                      startIcon={<VisibilityIcon />}
                      sx={{
                        backgroundColor: color,
                        "&:hover": {
                          backgroundColor: color,
                          filter: "brightness(0.9)",
                        },
                      }}
                    >
                      Browse Content
                    </Button>
                  </CardActions>
                </ProgramCard>
              </Grid>
            );
          })
        )}
      </Grid>

      {/* Empty State */}
      {!loading && !error && programs.length === 0 && (
        <Box textAlign="center" py={8}>
          <SchoolIcon sx={{ fontSize: 64, color: "text.disabled", mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No programs available
          </Typography>
        </Box>
      )}
    </Container>
  );
};

export default ContentPreviewLanding;
